import { create } from "zustand";
import { applyPatch as applyJsonPatch, type Operation } from "fast-json-patch";
import type { CellAddress, CellRange, TemplateDef } from "@juro/shared";
import type { JsonPatch } from "../types/ai.js";

type EditorStore = {
  templateId: string | null;
  draft: TemplateDef | null;
  savedVersion: TemplateDef | null;
  isDirty: boolean;
  undoStack: TemplateDef[];
  redoStack: TemplateDef[];
  selectedBlockId: string | null;
  selectedCell: CellAddress | null;
  selectedRange: CellRange | null;
  loadDraft: (templateId: string | null, template: TemplateDef) => void;
  clearSessionDraft: () => void;
  clearDraft: () => void;
  setDraft: (fn: (prev: TemplateDef) => TemplateDef) => void;
  applyPatch: (patch: JsonPatch[]) => void;
  undo: () => void;
  redo: () => void;
  resetToSaved: () => void;
  markSaved: (saved: TemplateDef) => void;
  selectBlock: (id: string | null) => void;
  selectCell: (blockId: string, rowId: string, colId: string) => void;
  clearSelection: () => void;
};

const HISTORY_LIMIT = 50;
const SESSION_DRAFT_KEY = "juro.template-editor.draft.v1";

type SessionDraft = Pick<
  EditorStore,
  "templateId" | "draft" | "savedVersion" | "isDirty" | "selectedBlockId" | "selectedCell" | "selectedRange"
>;

function pushHistory(stack: TemplateDef[], draft: TemplateDef): TemplateDef[] {
  return [...stack.slice(-(HISTORY_LIMIT - 1)), structuredClone(draft)];
}

function readSessionDraft(): SessionDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_DRAFT_KEY);
    return raw ? (JSON.parse(raw) as SessionDraft) : null;
  } catch {
    return null;
  }
}

function writeSessionDraft(snapshot: SessionDraft) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_DRAFT_KEY, JSON.stringify(snapshot));
  } catch {
    // Session storage can be unavailable in strict browser modes. The in-memory store still works.
  }
}

function removeSessionDraft() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SESSION_DRAFT_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function persistFromState(state: EditorStore) {
  if (!state.draft || !state.isDirty) {
    removeSessionDraft();
    return;
  }
  writeSessionDraft({
    templateId: state.templateId,
    draft: state.draft,
    savedVersion: state.savedVersion,
    isDirty: state.isDirty,
    selectedBlockId: state.selectedBlockId,
    selectedCell: state.selectedCell,
    selectedRange: state.selectedRange,
  });
}

const sessionDraft = readSessionDraft();

export const useEditorStore = create<EditorStore>((set, get) => ({
  templateId: sessionDraft?.templateId ?? null,
  draft: sessionDraft?.draft ? structuredClone(sessionDraft.draft) : null,
  savedVersion: sessionDraft?.savedVersion ? structuredClone(sessionDraft.savedVersion) : null,
  isDirty: sessionDraft?.isDirty ?? false,
  undoStack: [],
  redoStack: [],
  selectedBlockId: sessionDraft?.selectedBlockId ?? sessionDraft?.draft?.body.blocks[0]?.id ?? null,
  selectedCell: sessionDraft?.selectedCell ?? null,
  selectedRange: sessionDraft?.selectedRange ?? null,

  loadDraft: (templateId, template) => {
    set({
      templateId,
      draft: structuredClone(template),
      savedVersion: structuredClone(template),
      isDirty: false,
      undoStack: [],
      redoStack: [],
      selectedBlockId: template.body.blocks[0]?.id ?? null,
      selectedCell: null,
      selectedRange: null,
    });
    persistFromState(get());
  },

  clearSessionDraft: () => {
    removeSessionDraft();
  },

  clearDraft: () => {
    set({
      templateId: null,
      draft: null,
      savedVersion: null,
      isDirty: false,
      undoStack: [],
      redoStack: [],
      selectedBlockId: null,
      selectedCell: null,
      selectedRange: null,
    });
    removeSessionDraft();
  },

  setDraft: (fn) => {
    const { draft, undoStack } = get();
    if (!draft) return;
    set({
      draft: fn(structuredClone(draft)),
      undoStack: pushHistory(undoStack, draft),
      redoStack: [],
      isDirty: true,
    });
    persistFromState(get());
  },

  applyPatch: (patch) => {
    const { draft, undoStack } = get();
    if (!draft) return;
    const result = applyJsonPatch(structuredClone(draft), patch as Operation[], true, false).newDocument as TemplateDef;
    set({
      draft: result,
      undoStack: pushHistory(undoStack, draft),
      redoStack: [],
      isDirty: true,
    });
    persistFromState(get());
  },

  undo: () => {
    const { draft, undoStack, redoStack } = get();
    if (!draft || undoStack.length === 0) return;
    const previous = undoStack[undoStack.length - 1];
    if (!previous) return;
    set({
      draft: structuredClone(previous),
      undoStack: undoStack.slice(0, -1),
      redoStack: pushHistory(redoStack, draft),
      isDirty: true,
    });
    persistFromState(get());
  },

  redo: () => {
    const { draft, undoStack, redoStack } = get();
    if (!draft || redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    if (!next) return;
    set({
      draft: structuredClone(next),
      undoStack: pushHistory(undoStack, draft),
      redoStack: redoStack.slice(0, -1),
      isDirty: true,
    });
    persistFromState(get());
  },

  resetToSaved: () => {
    const saved = get().savedVersion;
    if (!saved) return;
    set({ draft: structuredClone(saved), isDirty: false, undoStack: [], redoStack: [] });
    persistFromState(get());
  },

  markSaved: (saved) => {
    set({
      savedVersion: structuredClone(saved),
      draft: structuredClone(saved),
      isDirty: false,
      undoStack: [],
      redoStack: [],
    });
    removeSessionDraft();
  },

  selectBlock: (id) => {
    set({ selectedBlockId: id, selectedCell: null, selectedRange: null });
    persistFromState(get());
  },
  selectCell: (blockId, rowId, colId) => {
    set({ selectedBlockId: blockId, selectedCell: { blockId, rowId, colId }, selectedRange: null });
    persistFromState(get());
  },
  clearSelection: () => {
    set({ selectedBlockId: null, selectedCell: null, selectedRange: null });
    persistFromState(get());
  },
}));
